/**
 * Bộ chọn kỳ theo NHÓM Ngày · Tháng · Năm, và tập NHIỀU kỳ để so với nhau (việc 49,
 * docs/13-tasks/02-terminal-share.md#49). Pure (S1): "hôm nay" do người gọi đưa vào.
 *
 * Địa chỉ giữ nguyên hợp đồng cũ — `preset` / `from` / `to` vẫn là MỘT kỳ mà mọi màn đọc. Tập kỳ
 * so sánh là hai khoá THÊM, chỉ màn Thị phần cảng đọc:
 * - `periods=2024,2025,2026` (trọn năm) hoặc `periods=2025-03,2026-03` (trọn tháng) — một loại mỗi
 *   lúc, tối thiểu hai kỳ mới là "tập";
 * - `ytd` — tập NĂM mặc định cắt mọi năm ở CÙNG ngày của hôm nay (lũy kế 01/01 → dd/mm), để năm
 *   đang chạy dở được so công bằng với cùng kỳ các năm trước (chủ dự án 27/10/2026: "thống kê hàng
 *   năm là so sánh cùng kỳ, không phải so toàn 12 tháng"). Chỉ `ytd=0` mới là trọn 12 tháng.
 *
 * 🚨 Khi có tập, `preset`/`from`/`to` luôn là kỳ MỚI NHẤT của tập — màn khác mở cùng địa chỉ vẫn
 * thấy một kỳ có nghĩa, và danh sách tàu dưới màn Thị phần đọc đúng kỳ ấy.
 */
import { FIRST_DATA_YEAR, monthRange, monthValue, type Period, type Preset } from './period.js';

export type PeriodGroup = 'day' | 'month' | 'year';
export type SetKind = 'year' | 'month';

export const PERIODS_PARAM = 'periods';
export const YTD_PARAM = 'ytd';

/**
 * Trần số kỳ so cùng lúc — agent tự quyết, bác thì tôi sửa. 8 cảng × 6 kỳ đã là 48 cột trên một
 * biểu đồ; hơn nữa thì độ đậm của cột không còn phân biệt được kỳ nào với kỳ nào.
 */
export const MAX_PERIODS = 6;

/** Màn nào biết đọc tập kỳ. Màn khác không hiện công tắc "so sánh nhiều kỳ". */
export const MULTI_PERIOD_PATHS: readonly string[] = ['/share/terminals'];

export const GROUP_LABELS: Readonly<Record<PeriodGroup, string>> = {
  day: 'Ngày',
  month: 'Tháng',
  year: 'Năm',
};

/** Các nút kỳ-lăn (tính theo hôm nay) của từng nhóm; năm không có — năm là các ô năm. */
export const GROUP_PRESETS: Readonly<Record<PeriodGroup, readonly Preset[]>> = {
  day: ['today', 'last7'],
  month: ['thisMonth', 'lastMonth', 'thisQuarter'],
  year: [],
};

export interface PeriodItem {
  /** `YYYY` hoặc `YYYY-MM`. */
  readonly token: string;
  readonly label: string;
  readonly from: string;
  readonly to: string;
}

export interface PeriodSet {
  readonly kind: SetKind;
  /** Cũ trước, mới sau — trái sang phải như dòng thời gian. */
  readonly items: readonly PeriodItem[];
  readonly ytd: boolean;
}

const YEAR_TOKEN = /^\d{4}$/;
const MONTH_TOKEN = /^\d{4}-(0[1-9]|1[0-2])$/;

const ddmm = (date: string) => `${date.slice(8, 10)}/${date.slice(5, 7)}`;

/**
 * Cùng ngày-tháng của hôm nay, đặt vào năm `year`. 29/02 rơi vào năm không nhuận ⇒ 28/02, không
 * tràn sang 01/03 (tràn là lấy thêm một ngày mà năm kia không có).
 */
export function sameDayIn(year: string, today: string): string {
  const monthDay = today.slice(5);
  if (monthDay === '02-29') {
    const leap = monthRange(`${year}-02`);
    return leap?.to ?? `${year}-02-28`;
  }
  return `${year}-${monthDay}`;
}

export function yearItem(year: string, today: string, ytd: boolean): PeriodItem {
  const to = ytd ? sameDayIn(year, today) : `${year}-12-31`;
  return {
    token: year,
    label: ytd ? `${year} (01/01–${ddmm(to)})` : year,
    from: `${year}-01-01`,
    to,
  };
}

export function monthItem(month: string): PeriodItem | null {
  const range = monthRange(month);
  if (range === null) return null;
  return {
    token: month,
    label: `T${String(Number(month.slice(5)))}/${month.slice(0, 4)}`,
    ...range,
  };
}

/** Tháng `YYYY-MM` dịch đi `months` tháng (âm là lùi). */
function shiftMonth(month: string, months: number): string {
  const index = Number(month.slice(0, 4)) * 12 + Number(month.slice(5, 7)) - 1 + months;
  const year = Math.floor(index / 12);
  return `${String(year)}-${String((index % 12) + 1).padStart(2, '0')}`;
}

/**
 * Ba trọn tháng của chế độ "So cả hai kỳ" (việc 56): cùng kỳ NĂM TRƯỚC · tháng TRƯỚC · tháng đang
 * xem, xếp cũ → mới như mọi tập kỳ khác. Kỳ đang xem không đúng bằng trọn một tháng ⇒ `[]` (không
 * có bộ ba nào để dựng).
 *
 * 🚨 Cả ba đều là TRỌN tháng lịch, kể cả tháng đang chạy dở: tháng 9 xem ngày 20 vẫn ôm 01→30/09,
 * đúng như hai chế độ so cũ. Chỗ khập khiễng ấy `partialNote` nói ra trên màn, không giấu.
 * Tháng lùi ra trước năm đầu có dữ liệu thì BỎ, chứ không dựng một kỳ rỗng để so.
 */
export function monthTriplet(period: Period, today: string): PeriodItem[] {
  const month = monthValue(period);
  if (month === '') return [];
  return [shiftMonth(month, -12), shiftMonth(month, -1), month]
    .filter((token) => usableMonth(token, today))
    .flatMap((token) => {
      const item = monthItem(token);
      return item === null ? [] : [item];
    });
}

/** Năm chọn được: từ năm đầu có dữ liệu tới năm nay. */
function usableYear(token: string, today: string): boolean {
  const year = Number(token);
  return YEAR_TOKEN.test(token) && year >= FIRST_DATA_YEAR && year <= Number(today.slice(0, 4));
}

/** Tháng chọn được: từ 01/2021 tới tháng này — tháng tương lai chưa có một con số nào. */
export function usableMonth(token: string, today: string): boolean {
  return (
    MONTH_TOKEN.test(token) &&
    Number(token.slice(0, 4)) >= FIRST_DATA_YEAR &&
    token <= today.slice(0, 7)
  );
}

/** Chuẩn hoá một danh sách mã kỳ: bỏ rác và trùng, xếp cũ → mới, cắt ở trần. */
export function cleanTokens(kind: SetKind, tokens: readonly string[], today: string): string[] {
  const usable = kind === 'year' ? usableYear : usableMonth;
  const unique = [...new Set(tokens.filter((token) => usable(token, today)))].sort();
  /* quá trần ⇒ giữ các kỳ MỚI nhất: kỳ mới nhất là kỳ cả trang đang đứng */
  return unique.slice(-MAX_PERIODS);
}

/**
 * Tập kỳ trên địa chỉ. Dưới hai kỳ hợp lệ ⇒ `null` (không phải một "tập" — màn vẫn so như cũ);
 * trộn năm với tháng ⇒ `null`: không có câu hỏi nào so 2025 với T3/2026.
 */
export function readPeriodSet(params: URLSearchParams, today: string): PeriodSet | null {
  const raw = (params.get(PERIODS_PARAM) ?? '').split(',').filter((token) => token !== '');
  if (raw.length < 2) return null;
  const kind: SetKind | null = raw.every((token) => YEAR_TOKEN.test(token))
    ? 'year'
    : raw.every((token) => MONTH_TOKEN.test(token))
      ? 'month'
      : null;
  if (kind === null) return null;
  const tokens = cleanTokens(kind, raw, today);
  if (tokens.length < 2) return null;
  /* 🚨 vắng `ytd` ⇒ CÙNG KỲ (việc 68): link cũ `periods=2024,2025,2026` từng so trọn năm với năm
     đang chạy dở — nay mở ra là so cùng kỳ */
  const ytd = kind === 'year' && params.get(YTD_PARAM) !== '0';
  const items = tokens.flatMap((token) => {
    const item = kind === 'year' ? yearItem(token, today, ytd) : monthItem(token);
    return item === null ? [] : [item];
  });
  return { kind, items, ytd };
}

/**
 * Mọi khoá kỳ cần ghi cho một lựa chọn năm/tháng — một kỳ hay cả tập. Kỳ đứng một mình mà trùng
 * một nút lăn ("Năm nay", "Lũy kế năm", "Tháng này") thì ghi đúng nút ấy, để địa chỉ gửi đi hôm sau
 * vẫn là "tháng này" chứ không đóng băng vào tháng cũ.
 */
export function periodSetParams(
  kind: SetKind,
  tokens: readonly string[],
  ytd: boolean,
  today: string,
): Record<string, string> {
  const clean = cleanTokens(kind, tokens, today);
  const latest = clean.at(-1);
  if (latest === undefined) return {};
  const cut = kind === 'year' && ytd;
  const single = ((): Record<string, string> => {
    if (kind === 'year' && latest === today.slice(0, 4))
      return { preset: cut ? 'yearToDate' : 'thisYear' };
    if (kind === 'month' && latest === today.slice(0, 7)) return { preset: 'thisMonth' };
    const item = kind === 'year' ? yearItem(latest, today, cut) : monthItem(latest);
    return item === null ? {} : { preset: 'custom', from: item.from, to: item.to };
  })();
  if (clean.length < 2) return single;
  return {
    ...single,
    [PERIODS_PARAM]: clean.join(','),
    ...(kind === 'year' ? { [YTD_PARAM]: cut ? '1' : '0' } : {}),
  };
}

/**
 * Thêm/bớt một kỳ trong tập. Không bao giờ bỏ kỳ CUỐI CÙNG (màn phải luôn có một kỳ để đứng), và
 * không thêm quá trần — nút ô đã khoá sẵn, đây là chốt thứ hai.
 */
export function toggleToken(tokens: readonly string[], token: string): string[] {
  if (tokens.includes(token))
    return tokens.length === 1 ? [...tokens] : tokens.filter((item) => item !== token);
  if (tokens.length >= MAX_PERIODS) return [...tokens];
  return [...tokens, token].sort();
}

/** Kỳ đang xem có đúng dáng "lũy kế năm Y" (01/01/Y → cùng ngày của hôm nay) không. */
function isYtdShape(period: Period, today: string): boolean {
  const year = period.from.slice(0, 4);
  return period.from === `${year}-01-01` && period.to === sameDayIn(year, today);
}

function isWholeYear(period: Period): boolean {
  const year = period.from.slice(0, 4);
  return period.from === `${year}-01-01` && period.to === `${year}-12-31`;
}

/** Nhóm của kỳ đang xem — nhóm nào sáng đèn trên thanh chọn kỳ. */
export function periodGroup(period: Period, set: PeriodSet | null, today: string): PeriodGroup {
  if (set !== null) return set.kind;
  switch (period.preset) {
    case 'today':
    case 'last7':
      return 'day';
    case 'thisMonth':
    case 'lastMonth':
    case 'thisQuarter':
      return 'month';
    case 'thisYear':
    case 'yearToDate':
      return 'year';
    case 'custom':
      if (isWholeYear(period) || isYtdShape(period, today)) return 'year';
      return monthValue(period) === '' ? 'day' : 'month';
  }
}

/** Lũy kế đang bật? Chỉ có nghĩa ở nhóm Năm. */
export function isYtd(period: Period, set: PeriodSet | null, today: string): boolean {
  if (set !== null) return set.ytd;
  if (period.preset === 'yearToDate') return true;
  return period.preset === 'custom' && !isWholeYear(period) && isYtdShape(period, today);
}

/** Các ô năm/tháng đang sáng — cả tập, hoặc đúng một ô khi kỳ đang xem trùng một ô. */
export function selectedTokens(period: Period, set: PeriodSet | null, today: string): string[] {
  if (set !== null) return set.items.map((item) => item.token);
  const group = periodGroup(period, null, today);
  if (group === 'year') return [period.from.slice(0, 4)];
  if (group === 'month') {
    const month = monthValue(period);
    return month === '' ? [] : [month];
  }
  return [];
}

/** Mười hai ô tháng của một năm, kèm ô nào chưa tới (khoá lại). */
export function monthCells(
  year: string,
  today: string,
): { token: string; label: string; future: boolean }[] {
  return Array.from({ length: 12 }, (_, index) => {
    const token = `${year}-${String(index + 1).padStart(2, '0')}`;
    return { token, label: `T${String(index + 1)}`, future: token > today.slice(0, 7) };
  });
}

/**
 * Câu cảnh báo khi một kỳ của tập CHƯA trọn: năm/tháng đang chạy dở so với các kỳ đã trọn thì
 * mũi tên giảm nào cũng vô nghĩa (cùng lý do với `elapsed` của `period.ts`). `null` ⇒ không cần nói.
 */
export function partialNote(set: PeriodSet, today: string): string | null {
  const running = set.items.find((item) => item.from <= today && item.to > today);
  if (running === undefined) return null;
  return set.kind === 'year'
    ? `Năm ${running.token} mới có số tới ${ddmm(today)} — bật “Lũy kế” để mọi năm cùng cắt ở ${ddmm(today)}.`
    : `${running.label} mới có số tới ${ddmm(today)}, các tháng kia là trọn tháng.`;
}

/**
 * Cùng câu ấy cho phép so HAI kỳ — "So tháng trước" · "So cùng kỳ năm trước" (việc 60,
 * docs/13-tasks/02-terminal-share.md#60). Ngày 01/10 "Tháng này" mới có một ngày số mà kỳ so sánh
 * của máy chủ là TRỌN tháng 9: màn in "−100,0 %" và không cột nào cho T10, người đọc tưởng mất dữ
 * liệu. Số trên màn không đổi; câu này chỉ NÓI RA vì sao. Kỳ đã trọn, kỳ chưa tới, hoặc chưa có
 * kỳ so sánh ⇒ `null`.
 */
export function pairPartialNote(
  period: { readonly from: string; readonly to: string },
  compared: { readonly from: string; readonly to: string } | null,
  today: string,
): string | null {
  if (compared === null) return null;
  if (period.from > today || period.to <= today) return null;
  return (
    `${periodLabel(period.from, period.to)} mới có số tới ${ddmm(today)}, còn ` +
    `${periodLabel(compared.from, compared.to)} là trọn kỳ — chênh lệch giữa hai kỳ chưa so được.`
  );
}

/**
 * Tên NGẮN của một khoảng ngày, đặt dưới chân cột và trên dòng chênh lệch khi so HAI kỳ (chủ dự án
 * 18/09/2026: không ghi "Kỳ này" · "Kỳ trước", ghi đúng kỳ ấy). Cùng cách viết với tập nhiều kỳ:
 * trọn năm "2026" · trọn quý "Q3/2026" · trọn tháng "T9/2026" · lũy kế năm "2026 (01/01–18/09)" ·
 * một ngày "18/09/2026" · còn lại "01/09–18/09/2026" (hai đầu khác năm thì ghi năm cả hai đầu).
 */
export function periodLabel(from: string, to: string): string {
  const year = from.slice(0, 4);
  const sameYear = to.slice(0, 4) === year;
  if (from === to) return `${ddmm(from)}/${year}`;
  if (sameYear && from.endsWith('-01-01') && to.endsWith('-12-31')) return year;
  const wholeMonths = sameYear && from.endsWith('-01') && monthRange(to.slice(0, 7))?.to === to;
  if (wholeMonths) {
    const first = Number(from.slice(5, 7));
    const last = Number(to.slice(5, 7));
    if (first === last) return `T${String(first)}/${year}`;
    if (last - first === 2 && first % 3 === 1) return `Q${String((first + 2) / 3)}/${year}`;
    return `T${String(first)}–T${String(last)}/${year}`;
  }
  if (sameYear && from.endsWith('-01-01')) return `${year} (01/01–${ddmm(to)})`;
  return sameYear
    ? `${ddmm(from)}–${ddmm(to)}/${year}`
    : `${ddmm(from)}/${year}–${ddmm(to)}/${to.slice(0, 4)}`;
}
